import { useState, useMemo, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, RefreshCw, Edit, Trash2, Globe, Phone } from 'lucide-react';
import { useSuppliers } from '@/hooks/useSuppliers';
import { useOptimizedData } from "@/hooks/useOptimizedData";
import { AdvancedSearch, SearchConfig } from "@/components/ui/search-advanced";
import { OptimizedTable, TableColumn } from "@/components/ui/optimized-table";
import { DataExport } from "@/components/ui/data-export";
import { SupplierDialog } from '@/components/suppliers/SupplierDialog';
import { ViewToggle } from '@/components/shared/ViewToggle';
import { SupplierCard } from '@/components/suppliers/SupplierCard';
import { Supplier } from '@/types/supplier';
import { Badge } from "@/components/ui/badge";
import { LoadingSpinnerWithText } from "@/components/ui/loading-spinner";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
export default function Suppliers() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [view, setView] = useState<'table' | 'cards'>('table');
  const {
    filteredSuppliers: allSuppliers,
    loading,
    handleDelete,
    fetchSuppliers,
    getUniqueCountries
  } = useSuppliers();

  // تكوين البحث المتقدم
  const searchConfig: SearchConfig = useMemo(() => ({
    placeholder: "البحث في الموردين...",
    filters: [{
      id: 'country',
      label: 'الدولة',
      type: 'select',
      options: getUniqueCountries().map(country => ({
        value: country,
        label: country
      })),
      icon: Globe
    }, {
      id: 'phone',
      label: 'رقم الهاتف',
      type: 'text',
      icon: Phone
    }]
  }), [getUniqueCountries]);

  // استخدام hook البيانات المحسن
  const {
    data: displaySuppliers,
    allData: filteredSuppliers,
    searchQuery,
    setSearchQuery,
    filters,
    addFilter,
    removeFilter,
    clearFilters,
    sortConfig,
    changeSort,
    pagination,
    changePage,
    stats
  } = useOptimizedData({
    data: allSuppliers,
    searchFields: ['country', 'email'],
    initialPageSize: 20
  });

  // الإشعارات في الوقت الفعلي
  useEffect(() => {
    const channel = supabase.channel('suppliers_changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'suppliers'
    }, payload => {
      console.log('Supplier change detected:', payload);
      fetchSuppliers();
      switch (payload.eventType) {
        case 'INSERT':
          toast.success('تم إضافة مورد جديد');
          break;
        case 'UPDATE':
          toast.info('تم تحديث بيانات مورد');
          break;
        case 'DELETE':
          toast.warning('تم حذف مورد');
          break;
      }
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchSuppliers]);

  // تكوين أعمدة الجدول
  const tableColumns: TableColumn<Supplier>[] = useMemo(() => [{
    key: 'name_ar',
    title: 'اسم المورد',
    dataIndex: 'name_ar',
    sortable: true,
    render: (_, record) => <div className="font-medium text-card-foreground">{record.name_ar}</div>
  }, {
    key: 'country',
    title: 'الدولة',
    dataIndex: 'country',
    sortable: true,
    render: (_, record) => record.country || '-'
  }, {
    key: 'mobile',
    title: 'رقم الهاتف',
    dataIndex: 'mobile',
    render: (_, record) => record.mobile || '-'
  }, {
    key: 'email',
    title: 'البريد الإلكتروني',
    dataIndex: 'email',
    render: (_, record) => record.email || '-'
  }, {
    key: 'actions',
    title: 'الإجراءات',
    align: 'center',
    render: (_, record) => <div className="flex gap-2 justify-center">
          <Button variant="ghost" size="sm" onClick={() => handleEdit(record)} className="h-8 w-8 p-0 hover:bg-primary-light">
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(record.id)} className="h-8 w-8 p-0 hover:bg-destructive-light text-destructive">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
  }], [handleDelete]);

  // معالجات الأحداث
  const handleEdit = useCallback((supplier: Supplier) => {
    setEditingSupplier(supplier);
    setDialogOpen(true);
  }, []);
  const handleAdd = useCallback(() => {
    setEditingSupplier(null);
    setDialogOpen(true);
  }, []);
  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setEditingSupplier(null);
    fetchSuppliers();
  }, [fetchSuppliers]);
  const handleSearch = useCallback((query: string, searchFilters: any[]) => {
    setSearchQuery(query);
    searchFilters.forEach(filter => {
      addFilter(filter.id, filter.value, filter.type);
    });
  }, [setSearchQuery, addFilter]);
  const searchFilters = useMemo(() => filters.map(filter => {
    const config = searchConfig.filters?.find(f => f.id === filter.id);
    return {
      ...filter,
      label: config?.label || filter.id,
      options: config?.options
    };
  }), [filters, searchConfig.filters]);
  const handleManualRefresh = useCallback(async () => {
    toast.info("جاري تحديث البيانات...");
    try {
      await fetchSuppliers();
      toast.success("تم تحديث البيانات بنجاح");
    } catch (error) {
      toast.error("حدث خطأ أثناء تحديث البيانات");
    }
  }, [fetchSuppliers]);
  if (loading) {
    return <div className="container mx-auto p-6">
        <LoadingSpinnerWithText text="جاري تحميل الموردين..." />
      </div>;
  }
  return <div className="container mx-auto p-6 space-y-6" dir="rtl">
      {/* الهيدر */}
      <div className="flex items-center gap-2 mb-6">
        <h1 className="text-2xl font-bold text-card-foreground">الموردون</h1>
        
        <ViewToggle view={view} onViewChange={setView} />
        
        <DataExport data={filteredSuppliers} columns={[{
          key: 'name',
          title: 'الاسم',
          dataIndex: 'name'
        }, {
          key: 'country',
          title: 'الدولة',
          dataIndex: 'country'
        }, {
          key: 'phone',
          title: 'الهاتف',
          dataIndex: 'phone'
        }, {
          key: 'email',
          title: 'البريد الإلكتروني',
          dataIndex: 'email'
        }]} filename="suppliers" formats={['csv', 'json']} />
        
        <Button onClick={handleManualRefresh} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 ml-2" />
          تحديث
        </Button>
        
        <Button onClick={handleAdd} className="gap-2">
          <Plus className="h-4 w-4" />
          إضافة مورد
        </Button>
      </div>

      {/* البحث المتقدم */}
      <AdvancedSearch config={searchConfig} onSearch={handleSearch} initialQuery={searchQuery} initialFilters={searchFilters} />

      {/* المحتوى */}
      {view === 'cards' ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {displaySuppliers.map(supplier => <SupplierCard key={supplier.id} supplier={supplier} onEdit={handleEdit} onDelete={() => handleDelete(supplier.id)} />)}
        </div> : <OptimizedTable columns={tableColumns} data={displaySuppliers} loading={loading} pagination={{
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: stats.filtered,
      showSizeChanger: true,
      pageSizeOptions: [10, 20, 50, 100]
    }} onPaginationChange={changePage} sortConfig={sortConfig ? {
      key: sortConfig.key as string,
      direction: sortConfig.direction
    } : undefined} onSortChange={config => config ? changeSort(config.key as keyof Supplier, config.direction) : changeSort('id')} rowKey="id" emptyText="لا يوجد موردين" size="middle" />}
      
      {stats.filtered === 0 && !loading && <div className="text-center py-12 text-muted-foreground">
          <p>لم يتم العثور على أي موردين.</p>
        </div>}

      <SupplierDialog open={dialogOpen} onOpenChange={setDialogOpen} supplier={editingSupplier} onClose={handleDialogClose} />
    </div>;
}